import {
  AddSimCardInput,
  OrderSimCardInput,
  SimCardAddressInput,
  SimCardType2,
} from "@phonero/common-graphql/types"
import React, { FC, useState } from "react"
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Button,
  Input,
  ResponsiveMode,
  WizardNavigationProps,
  useResponsiveMode,
} from "@phonero/phonero-ui-lib"
import {
  PuxContent,
  PuxFooter,
  PuxTypography,
  useTranslation,
} from "@phonero/pux-react"
import css from "./SimCardWizard.module.scss"
import {
  CheckBoxItem,
  EmbeddedSimEmail,
  InfoBox,
  SimCardClassification,
  StepButtons,
  ValidateEmail,
  ZipCodeDocument,
  useAppLazyQuery,
  SimCardProductFragment,
  SimCardFragment,
} from "@phonero/common-ux"
import { Address } from "../Address"
import { SelectSimCard, SelectSimCardData } from "../SelectSimcard"
import { SimCardOrderFragment } from "@phonero/common-ux"
interface IAddressStepProps {
  address: SimCardAddressInput
  card?: SimCardProductFragment | SimCardFragment | SimCardOrderFragment
  deliveryMethod: number | number[] | undefined
  setDeliveryMethod: (input: number | undefined) => void
  // otherAddress: SimCardAddressInput | undefined
  // setOtherAddress: (input: SimCardAddressInput | undefined) => void
  enableSimWithoutPin: boolean
  simWithoutPin: boolean
  setSimWithoutPin: (input: boolean) => void
  simCardClassification: SimCardClassification | undefined
  setSimCardClassification: (input: SimCardClassification | undefined) => void
  embeddedSimEmail: string | undefined
  setEmbeddedSimEmail: (input: string | undefined) => void
}
export type Data = (OrderSimCardInput | AddSimCardInput) & {cardType: SimCardType2}
export type AddressParams = {
  name?: string | null
  address1?: string | null
  address2?: string | null
  email?: string | null
  zipCodeId?: number | null
}
export const AddressStep: FC<
  IAddressStepProps &
    WizardNavigationProps & {
      setData: React.Dispatch<React.SetStateAction<Data>>
      data: Data
    }
> = ({
  onNext,
  onPrev,
  address,
  card,
  enableSimWithoutPin,
  simWithoutPin,
  setSimWithoutPin,
  deliveryMethod,
  setDeliveryMethod,
  data,
  setData,
  simCardClassification,
  setSimCardClassification,
  embeddedSimEmail,
  setEmbeddedSimEmail,
}) => {
  const responsiveMode = useResponsiveMode()
  const { t } = useTranslation()
  const [lookupZipCode, { data: zipData, called }] = useAppLazyQuery(
    ZipCodeDocument,
    {}
  )
  const cardType = data.cardType ?? card?.type
  if(!cardType) 
    throw new Error("Missing  data.cardType")

  const [selectSimCard, setSelectSimCard] = useState<SelectSimCardData>({
    simNumber: data?.iccid || "",
    simType: cardType,
    simNumberStatus: "Pending",
    iccIdFullLength: undefined,
  })
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  if (!called && address.zipCodeId) {
    const tempId = address.zipCodeId
    if (tempId) {
      const id = Number(tempId)
      lookupZipCode({ variables: { zipCode: { id } } })
    }
  }
  const onChangeDeliveryMethod = (_index: number) => {
    const index = Number(_index)
    if (isNaN(index)) {
      console.error("recevied NaN for index", _index)
      return null
    }
    setDeliveryMethod(index)
  }
  const onChangeSimCardClassification = (_index: number) => {
    const index = Number(_index)
    if (isNaN(index)) {
      console.error("recevied NaN for index", _index)
      return null
    }
    if (index === 0) {
      setSimCardClassification(SimCardClassification.Nano)
    } else if (index === 1) {
      setDeliveryMethod(undefined)
      setSimCardClassification(SimCardClassification.Embedded)
    }
  }
  const onEmbeddedSimEmailupdate = (email: string) => {
    setEmbeddedSimEmail(email)
  }
  const zipCodes = zipData?.zipCodes
  const getCity = () => {
    if (zipCodes && zipCodes.length > 0) {
      return zipCodes[0]?.city
    }
    return ""
  }
  const getZipCode = () => {
    if (zipCodes && zipCodes.length > 0) {
      return zipCodes[0]?.zipCode
    }
    return ""
  }
  const onSimChanged = (simData: SelectSimCardData) => {
    if (simData.simNumberStatus === "Valid") {
      setData((d) => ({ ...d, iccid: simData.iccIdFullLength }))
    } else {
      setData((d) => ({ ...d, iccid: undefined }))
    }
    setSelectSimCard(simData)
  }
  const tempAddress: AddressParams = {
    name: data?.address?.name ?? null,
    address1: data?.address?.address1 ?? null,
    address2: data?.address?.address2 ?? null,
    email: data?.address?.email ?? null,
    zipCodeId: data?.address?.zipCodeId ? +data.address.zipCodeId : null,
  }
  const setAddress: React.Dispatch<React.SetStateAction<AddressParams>> =
    useCallback(
      (address) => {
        return setData((d) => {
          const nextAddress =
            typeof address === "function" ? address(d.address as any) : address
          const next = {
            ...d,
            address: {
              ...d?.address,
              name: nextAddress.name,
              address1: nextAddress.address1,
              address2: nextAddress.address2,
              email: nextAddress.email,
              zipCodeId: nextAddress.zipCodeId
                ? nextAddress.zipCodeId.toString()
                : null,
            },
          }
          return next
        })
      },
      [setData]
    )

  const nextEnabled = (() => {
    if (!onNext) return false
    if (deliveryMethod === 0) {
      if (
        isPanelOpen === false &&
        simCardClassification === SimCardClassification.Nano
      )
        return false
      if (!data?.address?.name || data.address.name.length === 0) return false
      if (!data?.address?.address1 || data.address.address1.length === 0)
        return false
      if (!data?.address?.zipCodeId || data.address.zipCodeId === null)
        return false
    }
    if (deliveryMethod === 1 && !data?.iccid) return false
    if (
      simCardClassification === SimCardClassification.Embedded &&
      !ValidateEmail(embeddedSimEmail)
    )
      return false

    return true
  })()
  const simWithoutPinElement = (
    <>
      <CheckBoxItem
        style={{
          "--padding-start": 0,
          "--padding-top": 0,
          "--padding-bottom": 0,
        }}
        id={"simWithoutPin"}
        checked={simWithoutPin}
        onToggle={(e) => setSimWithoutPin(e.target.checked)}
        icon={null}
        label={t("AddSubscriptionWizard.Step4.WithoutPin")}
      />
    </>
  )

  return (
    <>
      <PuxContent>
        {(card?.price ?? 0) > 0 && (
          <div className={css.subheader}>
            {t("SimCard.PriceInfo", {
              postProcess: "markdownPostprocessor",
              context: cardType,
              price: card?.price.toString(),
            })}
          </div>
        )}
        {/* TODO: These should be changed to PuxRadioGroup/PuxItem when [PUX-30](https://phoneroas.atlassian.net/browse/PUX-30) is closed */}
        <Accordion
          /* className={css.radioContainerVertical} */

          className={
            responsiveMode === ResponsiveMode.Desktop
              ? css["radio-container-vertical"]
              : css["radio-container-horizontal"]
          }
          index={simCardClassification}
          onChange={(index: number) => onChangeSimCardClassification(index)}
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
        >
          <AccordionItem
            style={{ width: "95%", margin: "auto" }}
            className={css["radio-container-vertical-left"]}
          >
            <AccordionButton
              as={Button}
              left={
                /*  <input
                  checked={simCardClassification === SimCardClassification.Nano}
                  type="radio"
                  name="simcardclassification"
                  className={css.radioButton}
                  readOnly
                  tabIndex={-1}
                  style={{ backgroundColor: "var(--pux-color-tertiary)" }}
                ></input> */
                <Input
                  type="radio"
                  name="simcardclassification"
                  checked={simCardClassification === SimCardClassification.Nano}
                  readOnly
                  tabIndex={-1}
                  //style={{ backgroundColor: "green" }}
                  //style={{ color: "var(--pux-color-tertiary)" }}
                />
              }
            >
              <div slot="start">
                {/* TODO: Fix icons, AppIcon are not available here. */}
                <AppIconSIMKort
                  style={{ color: "var(--pux-color-primary)" }}
                  // className="text-primary"
                  fontSize={32}
                />
              </div>
              <div style={{ textAlign: "left", padding: "20px 10px" }}>
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {t("AddSubscriptionWizard.Step4.PhysicalSimCard")}
                  </div>
                  {responsiveMode === ResponsiveMode.Desktop && (
                    <div
                      className={css["radio-container-vertical-height"]}
                      style={{
                        alignItems: "center",
                        display: "flex",
                        color: "#040D2999",
                        fontWeight: 600,
                      }}
                    >
                      <PuxTypography variant="label" component={"span"}>
                        {t(
                          "AddSubscriptionWizard.Step4.PhysicalSimCardDescription"
                        )}
                      </PuxTypography>
                    </div>
                  )}
                </div>
              </div>
              {responsiveMode === ResponsiveMode.Mobile && (
                <div style={{ float: "right", marginLeft: "auto" }}>
                  <InfoBox
                    size={20}
                    noSpaceAround
                    activateStopPropagation
                    message={t(
                      "AddSubscriptionWizard.Step4.PhysicalSimCardDescription"
                    )}
                  />
                </div>
              )}
            </AccordionButton>
          </AccordionItem>
          <AccordionItem
            style={{ width: "95%", margin: "auto" }}
            className={css["radio-container-vertical-right"]}
          >
            <AccordionButton
              as={Button}
              left={
                <Input
                  type="radio"
                  name="simcardclassification"
                  checked={
                    simCardClassification === SimCardClassification.Embedded
                  }
                  readOnly
                  tabIndex={-1}
                />
              }
            >
              <div slot="start">
                <AppIconEmbeddedSim
                  style={{ color: "var(--pux-color-primary)" }}
                  fontSize={32}
                />
              </div>
              <div style={{ textAlign: "left", padding: "20px 10px" }}>
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {t("AddSubscriptionWizard.Step4.EmbeddedSimCard")}
                  </div>
                  {responsiveMode === ResponsiveMode.Desktop && (
                    <div
                      className={css["radio-container-vertical-height"]}
                      style={{
                        alignItems: "center",
                        display: "flex",
                        color: "#040D2999",
                        fontWeight: 600,
                      }}
                    >
                      <PuxTypography variant="label" component={"span"}>
                        {t(
                          "AddSubscriptionWizard.Step4.EmbeddedSimCardDescription"
                        )}
                      </PuxTypography>
                    </div>
                  )}
                </div>
              </div>
              {responsiveMode === ResponsiveMode.Mobile && (
                <div style={{ float: "right", marginLeft: "auto" }}>
                  <InfoBox
                    size={20}
                    noSpaceAround
                    activateStopPropagation
                    message={t(
                      "AddSubscriptionWizard.Step4.EmbeddedSimCardDescription"
                    )}
                  />
                </div>
              )}
            </AccordionButton>
          </AccordionItem>
        </Accordion>
        {responsiveMode === ResponsiveMode.Mobile ? (
          <div style={{ padding: "20px 12px" }}>
            <hr></hr>
          </div>
        ) : (
          <div style={{ padding: "2rem 4rem" }}>
            <hr></hr>
          </div>
        )}
        {simCardClassification === SimCardClassification.Nano && (
          <Accordion
            className={css["radio-container"]}
            index={deliveryMethod}
            onChange={(index: number) => onChangeDeliveryMethod(index)}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          >
            <AccordionItem style={{ backgroundColor: "#E1E1E1" }}>
              <AccordionButton
                as={Button}
                left={
                  <Input
                    type="radio"
                    name="deliverymethod"
                    checked={deliveryMethod === 0 && isPanelOpen}
                    readOnly
                    tabIndex={-1}
                  />
                }
                onClick={() => setIsPanelOpen(true)}
                style={{
                  backgroundColor: !isPanelOpen && "rgb(237, 238, 240)",
                }}
                className={css["accordionHover"]}
                // style={{
                //   justifyContent: "flex-start",
                //   alignItems: "flex-start",
                // }}
              >
                {t("AddSubscriptionWizard.Step4.OrderNew")}{" "}
              </AccordionButton>
              {isPanelOpen && (
                <>
                  <AccordionPanel
                    style={{
                      padding: "0 0 10px 0",
                      margin: "0",
                      backgroundColor: "#E1E1E1",
                    }}
                  >
                    <div className={css.newAddressContainer}>
                      <div>
                        <Address
                          address={tempAddress}
                          forceRequired={false}
                          setAddress={setAddress}
                          displayLabel={true}
                        />
                      </div>

                      <div
                        style={{
                          padding: "0 0 10px 0",
                          margin: "0px",
                          fontSize: "9px",
                        }}
                      >
                        {enableSimWithoutPin && simWithoutPinElement}
                      </div>
                    </div>
                  </AccordionPanel>
                </>
              )}

              {/* 
              {enableSimWithoutPin && simWithoutPinElement}
              {enableSimWithoutPin &&
                responsiveMode === ResponsiveMode.Desktop && (
                  <AccordionPanel nopadding>
                    {simWithoutPinElement}
                  </AccordionPanel>
                )}                 */}
            </AccordionItem>
            <AccordionItem>
              <AccordionButton
                as={Button}
                left={
                  <Input
                    type="radio"
                    name="deliverymethod"
                    checked={deliveryMethod === 1}
                    readOnly
                    tabIndex={-1}
                  />
                }
              >
                <div style={{ textAlign: "left" }}>
                  {/* {responsiveMode === ResponsiveMode.Desktop
                    ? t("AddSubscriptionWizard.Step4.ActivateExisting")
                    : t("AddSubscriptionWizard.Step4.ActivateSimCard")} */}
                  {t("AddSubscriptionWizard.Step4.ActivateExisting", {
                    count: 1,
                  })}
                </div>
              </AccordionButton>

              <AccordionPanel nopadding dark>
                {/* {responsiveMode === ResponsiveMode.Mobile && (
                  <div>{t("AddSubscriptionWizard.Step4.ActivateExisting")}</div>
                )} */}

                <div className={css["new-address-container"]}>
                  <SelectSimCard
                    simData={selectSimCard}
                    assignSimData={onSimChanged}
                  />
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
        {simCardClassification === SimCardClassification.Embedded && (
          <div className={css["embedded-sim-email-container"]}>
            <EmbeddedSimEmail
              email={embeddedSimEmail ?? ""}
              forceRequired={true}
              onUpdate={onEmbeddedSimEmailupdate}
            />
          </div>
        )}
      </PuxContent>
      <PuxFooter>
        {(onPrev || onNext) && (
          <StepButtons
            onPrev={onPrev}
            onNext={onNext}
            nextDisabled={!nextEnabled}
          />
        )}
      </PuxFooter>
    </>
  )
}
